<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <el-button
          :disabled="isLoading || isSaving"
          class="add-editorial-item-button"
          size="mini"
          type="primary"
          icon="el-icon-plus"
          @click="handleAddNewItem"
        >
          {{ $t('button.addNewItem') }}
        </el-button>
        <el-button
          :loading="isSaving"
          :disabled="!canSave"
          class="save-button"
          size="mini"
          type="danger"
          icon="el-icon-upload2"
          @click="handleSave"
        >
          {{ $t('button.save') }}
        </el-button>
      </div>
    </template>

    <div
      v-loading="isLoading"
      class="home-collections"
    >
      <home-collections-table
        ref="homeCollectionsTable"
        :collections="collections"
        :collection-types="collectionTypes"
        @updateOrder="handleUpdateItemOrder"
        @updateCollection="handleUpdateItem"
        @deleteCollection="handleDeleteItem"
      />
    </div>

  </sticky-header-layout>
</template>

<script>
import HomeCollectionsTable from './HomeCollectionsTable.vue'
import HomeCollectionsAPI from '../../api/home-collections'

import { getRandomHash } from '../../utils/helper'

export default {
  name: 'HomeCollections',
  components: {
    HomeCollectionsTable,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      collections: [],
      collectionTypes: [
        { value: 1001, label: 'Carousel', inputType: 'bannerIds' },
        { value: 201, label: 'Title Separator', inputType: 'titleSeparator' },
        { value: 2001, label: 'Editorial Content', inputType: 'bannerIds' },
        { value: 2002, label: 'Editorial Cover', inputType: 'bannerIds' },
        { value: 14, label: 'Banner with 3 Products', inputType: 'bannerIds' },
        { value: 15, label: 'Banner with 6 Products', inputType: 'bannerIds' },
        {
          value: 101, label: 'Shortcut Menu Grid', inputType: 'none', disabled: true,
        },
        { value: 3000, label: 'Festival Promotions', inputType: 'bannerIds' },
        { value: 3001, label: 'Flash Sales', inputType: 'none' },
        {
          value: 0, label: 'Unknown', inputType: 'none', disabled: true,
        },
      ],
      isLoading: false,
      isSaving: false,
    }
  },
  computed: {
    canSave() {
      return this.collections.length > 0 && !this.loading
    },
  },
  mounted() {
    this.getCollections(this.slug)
  },
  methods: {
    async getCollections(slug) {
      this.isLoading = true
      try {
        const response = await HomeCollectionsAPI.getCollection(slug)
        const collections = response.data

        this.collections = collections.map((collection) => {
          const collectionType = this.collectionTypes.find(type => type.value === collection.collection_type_id)
          collection.type = typeof collectionType === 'object'
            ? collectionType
            : {
              value: 0,
              label: 'unknown',
              inputType: 'none',
              disabled: true,
            }
          return collection
        })
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false
      }
    },
    async handleSave() {
      if (!this.validateCollections(this.collections)) {
        this.$message({
          message: 'Please fill in all data.',
          type: 'error',
        });
        return
      }

      this.cleanUpContentBeforePosting()

      this.isSaving = true
      this.isLoading = true
      try {
        await HomeCollectionsAPI.updateCollections(this.collections, this.slug)
        await this.getCollections(this.slug)
        this.$message({
          message: 'Saved',
          type: 'success',
        });
      } catch (error) {
        console.log(error)
      } finally {
        this.isSaving = false
        this.isLoading = false
      }
    },
    validateCollections(collections) {
      for (let i = 0; i < collections.length; i += 1) {
        const collection = collections[i]
        const hasValidInput = !!collection.banner_ids || !!(collection.title && collection.image) || collection.type.inputType === 'none'
        if (!hasValidInput) {
          return false
        }
      }
      return true
    },
    cleanUpContentBeforePosting() {
      this.collections = this.collections.map((collection) => {
        if (collection.type.value === 201) {
          collection.banner_ids = ''
        }
        return collection
      })
    },
    handleAddNewItem() {
      const defaultTypeId = 1001
      const collection = {
        id: getRandomHash(),
        collection_type_id: defaultTypeId,
        new: true, // This is very important for api to determine where it should create or update.
        type: this.collectionTypes.find(type => type.value === defaultTypeId),
        banner_ids: '',
        title: '',
        title_zh_hk: '',
        title_zh_cn: '',
        title_th_th: '',
        image: '',
      }
      this.collections.push(collection)

      this.$nextTick(() => {
        this.$refs.homeCollectionsTable.setSort()
      })
    },
    handleUpdateItemOrder({ oldIndex, newIndex }) {
      const targetRow = this.collections.splice(oldIndex, 1)[0]
      this.collections.splice(newIndex, 0, targetRow)
    },
    handleUpdateItem({ updatedCollection, itemIndex }) {
      this.$set(this.collections, itemIndex, updatedCollection)
    },
    handleDeleteItem(index) {
      this.collections.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
.home-collections {
  min-height: 200px;
  width: 100%;
}
</style>
